import { IconQuestionCircleOutlined } from "@suse-o11y/icons";
import { prop } from "@suse-o11y/prop";
import { effect } from "@suse-o11y/signal";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { FormFieldErrorMessage } from "~/components/form/error-message";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { Input } from "~/components/input";
import { Textarea } from "~/components/textarea";
import { type QueryView, Type } from "~/models/dto";

import { queryQueryNodesList } from "../../../queries";
import { useQuery } from "../../../queries/react-query";
import { DocLink } from "../../react/docLink/DocLink";

import type { FormState } from "./_form-state";

export interface ViewFormProps {
	initial: QueryView;
	state: FormState;
}

export const ViewForm: React.FC<ViewFormProps> = ({ initial, state }) => {
	const { data: existingQueryViewsNames } = useQuery({
		...queryQueryNodesList(Type.QueryView),
		refetchOnMount: false,
		select: React.useCallback(
			(views: ReadonlyArray<QueryView>) => {
				const otherViews = initial.id == null ? views : views.filter((view) => view.id !== initial.id);

				return otherViews.map(prop("name"));
			},
			[initial.id],
		),
	});

	React.useEffect(
		() =>
			effect((monitor) => {
				const name = state.fields.name.getInput(monitor).trim();

				if (existingQueryViewsNames?.includes(name)) {
					state.fields.name.setError(["This view name is already used"]);
				} else {
					state.fields.name.setError(null);
				}
			}),
		[state.fields.name, existingQueryViewsNames],
	);

	return (
		<div className="form-fieldset">
			<FormField className="form-field" headless state={state.fields.name}>
				{(monitor, field) => (
					<div className="form-field-label">
						<FormLabel headless>Name *</FormLabel>

						{initial.id != null && (
							<button
								className="form-field-reset"
								disabled={!field.isDirty(monitor)}
								onClick={() => field.reset()}
								type="button"
							>
								Reset
							</button>
						)}
					</div>
				)}

				{(monitor, field) => (
					<Input
						autoFocus
						name="view-name"
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="Keep it short and sweet"
						value={field.getInput(monitor)}
					/>
				)}

				<FormFieldErrorMessage className="form-field-message-error form-field-message" headless />
			</FormField>

			<FormField className="form-field" headless state={state.fields.description}>
				{(monitor, field) => (
					<div className="form-field-label">
						<FormLabel headless>Description</FormLabel>

						{initial.id != null && (
							<button
								className="form-field-reset"
								disabled={!field.isDirty(monitor)}
								onClick={() => field.reset()}
								type="button"
							>
								Reset
							</button>
						)}
					</div>
				)}

				{(monitor, field) => (
					<Textarea
						name="view-description"
						onChange={(event) => field.setInput(event.target.value)}
						rows={5}
						value={field.getInput(monitor)}
					/>
				)}

				<FormFieldErrorMessage className="form-field-message-error form-field-message" headless />
			</FormField>

			<FormField className="form-field" headless state={state.fields.identifier}>
				{(monitor, field) => (
					<div className="form-field-label">
						<FormLabel headless>Identifier</FormLabel>

						<TooltipBoundary>
							<TooltipTrigger className="button button-xs button-square">
								<IconQuestionCircleOutlined className="text-core-icon-neutral-secondary" />
							</TooltipTrigger>

							<Tooltip className="tooltip/tooltip">
								Identifiers are URNs used for uniquely identifying nodes and edges (relations).
								<DocLink capture="noop" slug="ui-popover-identifiers-help">
									Learn more
								</DocLink>
							</Tooltip>
						</TooltipBoundary>

						{initial.id != null && (
							<button
								className="form-field-reset"
								disabled={!field.isDirty(monitor)}
								onClick={() => field.reset()}
								type="button"
							>
								Reset
							</button>
						)}
					</div>
				)}

				{(monitor, field) => (
					<Input
						name="view-identifier"
						onChange={(event) => field.setInput(event.target.value)}
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>
		</div>
	);
};
